import React, { useState } from 'react';
import { useDialog } from '../context/DialogContext';
import { progressService } from '../services/progressService';
import { formatDateWithRelative, calculateDuration } from '../utils/dateUtils';
import styles from '../styles/historyModal.module.css';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  stepNumber: number;
  stepTitle: string;
  stepData: any;
  onStepUpdated?: () => void;
}

interface TimelineItem {
  date: string;
  action: string;
  isStepEvent: boolean;
  isInProgress?: boolean;
}

const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  stepNumber,
  stepTitle,
  stepData,
  onStepUpdated
}) => {
  const { showConfirm, showAlert } = useDialog();
  const [isEditing, setIsEditing] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [newCompletedDate, setNewCompletedDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  // Build timeline from step data
  const buildTimeline = (): TimelineItem[] => {
    const timeline: TimelineItem[] = [];
    
    // Add step start
    if (stepData.started_at) {
      timeline.push({
        date: stepData.started_at,
        action: `${stepTitle} started`,
        isStepEvent: true
      });
    }
    
    // Add step completion - show even if not completed yet (with "In progress")
    if (stepData.started_at) {
      if (stepData.completed && stepData.completed_at) {
        timeline.push({
          date: stepData.completed_at,
          action: `${stepTitle} completed`,
          isStepEvent: true
        });
      } else {
        // Step is started but not completed - show "In progress"
        // Use current date for sorting so it appears after the start date
        timeline.push({
          date: new Date().toISOString(), // Use current date for sorting
          action: `${stepTitle} completed`,
          isStepEvent: true,
          isInProgress: true // Flag to indicate this is an in-progress item
        });
      }
    }
    
    // Sort by date
    return timeline.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const timeline = buildTimeline();
  const duration = stepData.started_at && stepData.completed_at 
    ? calculateDuration(stepData.started_at, stepData.completed_at)
    : stepData.started_at 
    ? calculateDuration(stepData.started_at, new Date().toISOString())
    : 0;

  const handleEditDates = () => {
    setIsEditing(true);
    setNewStartDate(stepData.started_at ? stepData.started_at.split('T')[0] : '');
    setNewCompletedDate(stepData.completed_at ? stepData.completed_at.split('T')[0] : '');
  };

  const handleSaveDates = async () => {
    if (!newStartDate) {
      showAlert({
        message: 'Please enter a start date',
        variant: 'warning'
      });
      return;
    }

    // Validate start date
    const startDate = new Date(newStartDate);
    if (isNaN(startDate.getTime())) {
      showAlert({
        message: 'Please enter a valid start date',
        variant: 'warning'
      });
      return;
    }

    if (startDate > new Date()) {
      showAlert({
        message: 'Start date cannot be in the future',
        variant: 'warning'
      });
      return;
    }

    // Validate completion date if provided
    if (newCompletedDate) {
      const completedDate = new Date(newCompletedDate);
      if (isNaN(completedDate.getTime())) {
        showAlert({
          message: 'Please enter a valid completion date',
          variant: 'warning'
        });
        return;
      }

      if (completedDate > new Date()) {
        showAlert({
          message: 'Completion date cannot be in the future',
          variant: 'warning'
        });
        return;
      }

      if (completedDate < startDate) {
        showAlert({
          message: 'Completion date must be after start date',
          variant: 'warning'
        });
        return;
      }
    }

    showConfirm({
      message: 'Edit Step Dates',
      onConfirm: async () => {
        setIsLoading(true);
        try {
          const updateData: any = { started_at: new Date(newStartDate).toISOString() };
          if (newCompletedDate) {
            updateData.completed_at = new Date(newCompletedDate).toISOString();
          }
          
          await progressService.updateStepDates(stepNumber, updateData);
          
          showAlert({
            message: 'Dates updated successfully',
            variant: 'success'
          });
          
          setIsEditing(false);
          onStepUpdated?.();
        } catch (error) {
          console.error('Error updating dates:', error);
          showAlert({
            message: error instanceof Error ? error.message : 'Failed to update dates',
            variant: 'danger'
          });
        } finally {
          setIsLoading(false);
        }
      },
      variant: 'info'
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewStartDate('');
    setNewCompletedDate('');
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>{stepTitle} Timeline</h3>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className={styles.content}>
          {timeline.length === 0 ? (
            <p className={styles.noData}>As you start completing the actions to work this step, you will see your progress timeline tracked here</p>
          ) : (
            <>
              <div className={styles.timeline}>
                {timeline.map((item, index) => (
                  <div key={index} className={`${styles.timelineItem} ${item.isStepEvent ? styles.stepEvent : styles.actionEvent}`}>
                    <span className={styles.action}>
                      {item.isStepEvent ? item.action : `- ${item.action}`}
                    </span>
                    <span className={styles.date}>
                      {item.isInProgress 
                        ? 'In progress' 
                        : formatDateWithRelative(item.date)}
                    </span>
                    <span className={styles.editColumn}>
                      {item.isStepEvent && (item.action.includes('started') || item.action.includes('completed')) && !item.isInProgress && (
                        <button 
                          className={styles.inlineEditButton}
                          onClick={handleEditDates}
                          disabled={isLoading}
                          title="Edit dates"
                        >
                          Edit
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              
              {duration > 0 && (
                <div className={styles.duration}>
                  Duration: {duration} day{duration !== 1 ? 's' : ''}
                </div>
              )}
            </>
          )}
        </div>
        
        {isEditing && (
          <div className={styles.editModal}>
            <div className={styles.editContent}>
              <h4>Edit Step Dates</h4>
              <div className={styles.dateInput}>
                <label htmlFor="startDate">Start Date:</label>
                <input
                  id="startDate"
                  type="date"
                  value={newStartDate}
                  onChange={(e) => setNewStartDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              {stepData.completed && (
                <div className={styles.dateInput}>
                  <label htmlFor="completedDate">Completion Date:</label>
                  <input
                    id="completedDate"
                    type="date"
                    value={newCompletedDate}
                    onChange={(e) => setNewCompletedDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    min={newStartDate}
                  />
                </div>
              )}
              <div className={styles.editActions}>
                <button 
                  className={styles.saveButton}
                  onClick={handleSaveDates}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
                <button 
                  className={styles.cancelButton}
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryModal; 